import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  useBlocker,
  useLocation,
  type Blocker,
  type BlockerFunction,
  type NavigationType,
} from 'react-router-dom';
import {
  topBlockingSource,
  UnsavedGuardContext,
  type GuardRegistry,
  type GuardSource,
} from './unsaved-guard-context';

/** What the ref holds once a held navigation has been answered: nothing is blocked. */
const NOTHING_HELD = {
  state: 'unblocked',
  reset: undefined,
  proceed: undefined,
  location: undefined,
} as Blocker;

/**
 * The app's single navigation blocker (TMMAPP-0073, TMM-B5). Mount it once, under the data
 * router, around everything an editor can render in (`AppShell`); `useUnsavedGuard` registers
 * with it. The app runs `createBrowserRouter`, a data router, which `useBlocker` requires.
 */
export function UnsavedGuardProvider({ children }: { children: ReactNode }) {
  const sources = useRef(new Set<GuardSource>());
  // The provider's own teardown runs before its editors' cleanups: nothing is answered after it.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const location = useLocation();
  const currentRef = useRef(location);
  useEffect(() => {
    currentRef.current = location;
  }, [location]);

  // The action of the navigation held last, so it is re-judged the same way when its source goes.
  const heldAction = useRef<NavigationType | undefined>(undefined);
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation, historyAction }) => {
      const blocks =
        topBlockingSource(sources.current, currentLocation, nextLocation, historyAction) !==
        undefined;
      if (blocks) heldAction.current = historyAction;
      return blocks;
    },
    [],
  );
  const blocker = useBlocker(shouldBlock);
  const blockerRef = useRef(blocker);
  useEffect(() => {
    blockerRef.current = blocker;
  }, [blocker]);

  // A navigation is held: the topmost dirty editor asks. If the work went away in the meantime
  // (a save landed, the editor closed) there is nothing to ask about, and the navigation continues.
  const settle = useCallback(() => {
    const held = blockerRef.current;
    if (!alive.current || held.state !== 'blocked') return;
    const top = topBlockingSource(
      sources.current,
      currentRef.current,
      held.location,
      heldAction.current,
    );
    if (top !== undefined) {
      // A page editor asks (it has no dialog of its own): a clean dialog still open over the page
      // closes first, so the page's question shows alone, never over it (DLG-005, TMMAPP-0097
      // §C 26). A dialog's own question shows in that dialog, its in-place steps included.
      if (top.dismissClean === undefined) {
        for (const source of sources.current) {
          // A dialog whose save is in flight stays: it closes when the save lands.
          if (source !== top && !source.dirty && !source.busy) source.dismissClean?.();
        }
      }
      top.ask(true);
      return;
    }
    // Answered once: the ref is cleared so a later cleanup never answers a stale blocker.
    blockerRef.current = NOTHING_HELD;
    held.proceed();
  }, []);
  useEffect(() => {
    settle();
  }, [blocker, settle]);

  const registry = useMemo<GuardRegistry>(() => {
    const clearQuestions = () => {
      for (const source of sources.current) source.ask(false);
    };
    return {
      register(source) {
        sources.current.add(source);
        return () => {
          sources.current.delete(source);
          settle();
        };
      },
      touch(source) {
        if (!sources.current.delete(source)) return;
        sources.current.add(source);
      },
      proceed() {
        clearQuestions();
        const held = blockerRef.current;
        blockerRef.current = NOTHING_HELD;
        if (held.state === 'blocked') held.proceed();
      },
      stay() {
        clearQuestions();
        const held = blockerRef.current;
        blockerRef.current = NOTHING_HELD;
        if (held.state === 'blocked') held.reset();
      },
    };
  }, [settle]);

  return <UnsavedGuardContext.Provider value={registry}>{children}</UnsavedGuardContext.Provider>;
}
